"use client";

import { type FormEvent, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useRun } from "@/components/tools/reference-tools/use-run";
import { MeshBuilder } from "@/components/tools/research-discovery/mesh-builder";
import { MeshDetail } from "@/components/tools/research-discovery/mesh-detail";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  loadDescriptor,
  type MeshCandidate,
  type MeshDescriptor,
  type MeshProblem,
  type SearchOutcome,
  searchMesh,
} from "@/lib/tool-kit/research-discovery/mesh";

/** English strings. A translated content file overrides these through `ui`. */
export const MESH_TERM_FINDER_STRINGS = {
  inputLabel: "Topic, heading or entry term",
  inputPlaceholder: "heart attack",
  inputHint:
    "Pressing Search sends what you typed from your browser to the U.S. National Library of Medicine's MeSH service (id.nlm.nih.gov) — nothing goes to CitationEasy. MeSH is produced by the National Library of Medicine.",
  search: "Search MeSH",
  searching: "Searching…",
  emptyPrompt:
    "Search for a topic to find its MeSH heading, entry terms, tree numbers and a PubMed search string.",
  tooShort: "Type at least two characters.",
  rateLimited: "The MeSH service is busy right now. Try again in a minute.",
  failed:
    "The MeSH service did not answer. Check your connection and try again.",
  noMatches:
    "No MeSH heading or entry term matches that text. Try a single word, a more general term, or the singular form.",
  termsUnresolved:
    "Some entry-term matches could not be traced to their headings just now, so only direct heading matches are shown. Try again to include them.",
  resultsHeading: "MeSH results",
  headingsLabel: "Headings that match",
  viaTermsLabel: "Found through an entry term",
  viaTerm: "{label} (via “{term}”)",
  loadingHeading: "Loading the heading…",
  headingFailed: "That heading could not be loaded. Try again.",
  kinds: {
    topical: "Topical heading",
    geographical: "Geographical heading",
    "publication-type": "Publication type",
    "check-tag": "Check tag",
  },
  introduced: "Added to MeSH in {year}",
  scopeNote: "Definition:",
  annotation: "Note to indexers:",
  previousIndexing: "Previously indexed as:",
  treeNumbers: "Tree numbers",
  treeLevel: "level {level}",
  treeCategories: {
    A: "Anatomy",
    B: "Organisms",
    C: "Diseases",
    D: "Chemicals and Drugs",
    E: "Analytical, Diagnostic and Therapeutic Techniques, and Equipment",
    F: "Psychiatry and Psychology",
    G: "Phenomena and Processes",
    H: "Disciplines and Occupations",
    I: "Anthropology, Education, Sociology, and Social Phenomena",
    J: "Technology, Industry, and Agriculture",
    K: "Humanities",
    L: "Information Science",
    M: "Named Groups",
    N: "Health Care",
    V: "Publication Characteristics",
    Z: "Geographicals",
  },
  entryTerms: "Entry terms",
  noEntryTerms: "This heading has no other entry terms.",
  broader: "Broader headings — click to open",
  narrower: "Narrower headings — click to open",
  seeAlso: "See also",
  listCut: "Showing {shown} of {total}.",
  subheadings: "Subheadings you can add:",
  addToSearch: "Add to PubMed string",
  added: "Added",
  copyEntryTerms: "Copy heading and entry terms",
  copied: "Copied!",
  browserLink: "View in the MeSH Browser",
  builderHeading: "PubMed search string",
  basketEmpty:
    "Add one or more headings and the string appears here, with the headings joined by OR.",
  remove: "Remove",
  optionsLegend: "Options",
  optExplode: "Include narrower headings (“explode” — PubMed's default)",
  optMajor: "Only articles where the heading is a major topic",
  optFreeText: "Add the heading and its entry terms as title/abstract words",
  chooseWords: "Choose the title/abstract words",
  copyString: "Copy search string",
  openPubmed: "Open in PubMed",
  clearBasket: "Clear",
  builderNote:
    "Paste this into PubMed's search box or Advanced search. It is one concept block — build one block per concept of your question and combine them with AND. Check the result in PubMed before relying on it.",
} as const;

export type MeshTermFinderStrings = typeof MESH_TERM_FINDER_STRINGS;

function Candidates({
  heading,
  items,
  strings,
  onOpen,
  current,
}: {
  heading: string;
  items: readonly MeshCandidate[];
  strings: MeshTermFinderStrings;
  onOpen: (id: string) => void;
  current: string;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <fieldset className="space-y-2">
      <legend className={LABEL}>{heading}</legend>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <button
            aria-pressed={item.id === current}
            className={item.id === current ? BUTTON_PRIMARY : BUTTON_SECONDARY}
            key={item.id}
            onClick={() => onOpen(item.id)}
            type="button"
          >
            {item.via
              ? t(strings.viaTerm, { label: item.label, term: item.via })
              : item.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function MeshTermFinderWidget() {
  const strings = useToolUi(MESH_TERM_FINDER_STRINGS);
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null);
  const [problem, setProblem] = useState<MeshProblem | "noMatches" | null>(
    null
  );
  const [selected, setSelected] = useState<MeshDescriptor | null>(null);
  const [headingProblem, setHeadingProblem] = useState(false);
  const [basket, setBasket] = useState<MeshDescriptor[]>([]);
  const searchRun = useRun();
  const headingRun = useRun();

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const controller = searchRun.start(1);
    try {
      const result = await searchMesh(query, controller.signal);
      if (!searchRun.isCurrent(controller)) {
        return;
      }
      if (!result.ok) {
        setOutcome(null);
        setProblem(result.problem);
        return;
      }
      const empty =
        result.outcome.headings.length + result.outcome.viaTerms.length === 0;
      setOutcome(empty ? null : result.outcome);
      setProblem(empty ? "noMatches" : null);
    } catch {
      // Aborted by a newer search or by leaving the page.
    } finally {
      searchRun.finish(controller);
    }
  }

  async function handleOpen(id: string) {
    setHeadingProblem(false);
    const controller = headingRun.start(1);
    try {
      const result = await loadDescriptor(id, controller.signal);
      if (!headingRun.isCurrent(controller)) {
        return;
      }
      if (result.ok) {
        setSelected(result.descriptor);
      } else {
        setHeadingProblem(true);
      }
    } catch {
      // Aborted by a newer request or by leaving the page.
    } finally {
      headingRun.finish(controller);
    }
  }

  function addSelected() {
    if (selected && !basket.some((item) => item.id === selected.id)) {
      setBasket((current) => [...current, selected]);
    }
  }

  return (
    <section className="space-y-6">
      <form className="space-y-4" onSubmit={handleSearch}>
        <TextField
          hint={strings.inputHint}
          id="mtf-query"
          label={strings.inputLabel}
          onChange={setQuery}
          placeholder={strings.inputPlaceholder}
          type="search"
          value={query}
        />
        <button
          className={BUTTON_PRIMARY}
          disabled={searchRun.running || query.trim() === ""}
          type="submit"
        >
          {searchRun.running ? strings.searching : strings.search}
        </button>
      </form>
      <div aria-live="polite" className="space-y-6">
        {problem ? <ErrorNotice>{strings[problem]}</ErrorNotice> : null}
        {outcome || problem || searchRun.running ? null : (
          <Notice>{strings.emptyPrompt}</Notice>
        )}
        {outcome ? (
          <div className={`${RESULT_CARD} space-y-5`}>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            {outcome.termsUnresolved ? (
              <Notice>{strings.termsUnresolved}</Notice>
            ) : null}
            <Candidates
              current={selected?.id ?? ""}
              heading={strings.headingsLabel}
              items={outcome.headings}
              onOpen={handleOpen}
              strings={strings}
            />
            <Candidates
              current={selected?.id ?? ""}
              heading={strings.viaTermsLabel}
              items={outcome.viaTerms}
              onOpen={handleOpen}
              strings={strings}
            />
            {headingRun.running ? (
              <p className={HINT}>{strings.loadingHeading}</p>
            ) : null}
            {headingProblem ? (
              <ErrorNotice>{strings.headingFailed}</ErrorNotice>
            ) : null}
          </div>
        ) : null}
        {selected ? (
          <MeshDetail
            descriptor={selected}
            inBasket={basket.some((item) => item.id === selected.id)}
            onAdd={addSelected}
            onOpen={handleOpen}
            strings={strings}
          />
        ) : null}
        {outcome || basket.length > 0 ? (
          <MeshBuilder
            basket={basket}
            onClear={() => setBasket([])}
            onRemove={(id) =>
              setBasket((current) => current.filter((item) => item.id !== id))
            }
            strings={strings}
          />
        ) : null}
      </div>
    </section>
  );
}
